import { useSearchParams } from "react-router-dom";

export type FilterValueType = string | number | boolean;

type FilterField = {
  type: "string" | "number" | "boolean";
  defaultValue?: FilterValueType;
};

interface FilterConfig<T> {
  fields: {
    [K in keyof T]: FilterField;
  };
}

type FilterState<T> = {
  [K in keyof T]: T[K] | undefined;
};

export const useFilterState = <T extends { [key: string]: FilterValueType }>(config: FilterConfig<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const parseValue = (
    value: string | null,
    type: "string" | "number" | "boolean",
    defaultValue?: FilterValueType
  ): FilterValueType | undefined => {
    if (value === null) return defaultValue;

    switch (type) {
      case "number":
        return value ? Number(value) : defaultValue;
      case "boolean":
        return value ? value === "true" : defaultValue;
      default:
        return value || defaultValue;
    }
  };

  const filters = Object.entries(config.fields).reduce<Partial<T>>((acc, [key, field]) => {
    const value = searchParams.get(key);
    acc[key as keyof T] = parseValue(value, field.type, field.defaultValue) as T[keyof T] | undefined;
    return acc;
  }, {}) as FilterState<T>;

  const queryParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined) queryParams.append(key, value.toString());
  });
  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

  const updateFilters = (key: keyof T, value: FilterValueType | undefined) => {
    const newParams = new URLSearchParams(searchParams);
    if (value !== undefined && value !== "") {
      newParams.set(key as string, value.toString());
    } else {
      newParams.delete(key as string);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    const newParams = new URLSearchParams(searchParams);
    Object.keys(filters).forEach((key) => {
      newParams.delete(key);
    });
    setSearchParams(newParams);
  };

  const hasActiveFilters = Object.entries(filters).some(
    ([key, value]) => value !== undefined && value !== config.fields[key as keyof T].defaultValue
  );

  return {
    filters,
    queryString,
    hasActiveFilters,
    updateFilters,
    clearFilters,
  };
};
